'use client'

import { useState } from 'react'
import { ChevronDown } from 'lucide-react'

const FAQS = [
  {
    q: 'Do I need to be good with computers?',
    a: 'No. BuilderBuddi is designed for contractors who spend most of the day on site, not at a desk. If you can use your phone for messages, photos, and email, you can start with one job and learn the rest as you go.',
  },
  {
    q: 'Can I use BuilderBuddi from my phone?',
    a: 'Yes. BuilderBuddi works from your mobile browser and can be installed to your phone as an app-like experience. You can quote jobs, add site notes, capture receipts, check approvals, and follow up while you are on site, in the van, or between jobs.',
  },
  {
    q: 'Do I need a credit card to start?',
    a: 'No. You can start free with no credit card required. Try BuilderBuddi with one real job, keep your current process while you test it, and upgrade only when it is clearly helping.',
  },
  {
    q: 'Is BuilderBuddi only for builders?',
    a: 'No. BuilderBuddi is for trade contractors and small trade teams, including painters, tilers, roofers, plumbers, electricians, renovators, landscapers, handymen, and small builders.',
  },
  {
    q: 'Can clients approve quotes online?',
    a: 'Yes. Your client receives a secure link, reviews the quote from their browser, and can approve it from their phone. They do not need an account or an app download.',
  },
  {
    q: 'Can I track receipts and job costs?',
    a: 'Yes. You can capture receipts, fuel, materials, and other job costs so records stay linked to the right project instead of being scattered across messages, pockets, and paper folders.',
  },
  {
    q: 'Can I cancel anytime?',
    a: 'Yes. Paid plans are billed monthly and can be cancelled when you need to. The free plan is available for getting started without pressure.',
  },
  {
    q: 'Is there an app?',
    a: 'BuilderBuddi can be installed to your phone as an app-like experience now, with the same login and data. Native app store versions are planned.',
  },
]

export function MarketingFaq() {
  const [openIndex, setOpenIndex] = useState<number | null>(0)

  return (
    <section id="faq" className="bg-white py-11 sm:py-20">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <div className="mb-8 text-left sm:mb-11 sm:text-center">
          <p className="mb-3 text-xs font-semibold uppercase text-[#c2571a]">Questions</p>
          <h2 className="text-[1.95rem] font-bold text-slate-900 sm:text-5xl">
            Common questions, clear answers
          </h2>
          <p className="mt-3 text-[1rem] leading-relaxed text-slate-600 sm:text-[1.18rem] sm:text-slate-500">
            Straight answers before you sign up.
          </p>
        </div>

        <div className="space-y-2">
          {FAQS.map((faq, i) => (
            <div
              key={faq.q}
              className={`rounded-2xl border transition-all duration-200 ${
                openIndex === i
                  ? 'border-[#1e3a5f]/20 bg-[#1e3a5f]/[0.02]'
                  : 'border-slate-200 bg-slate-50 hover:border-slate-300 hover:bg-white'
              }`}
            >
              <button
                type="button"
                className="flex w-full items-center justify-between gap-4 px-4 py-4 text-left sm:px-5"
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
              >
                <span
                  className={`text-[16px] font-semibold leading-snug transition-colors sm:text-[17px] ${
                    openIndex === i ? 'text-[#1e3a5f]' : 'text-slate-800'
                  }`}
                >
                  {faq.q}
                </span>
                <span
                  className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full transition-all duration-200 ${
                    openIndex === i ? 'rotate-180 bg-[#1e3a5f] text-white' : 'bg-slate-200 text-slate-500'
                  }`}
                >
                  <ChevronDown size={14} strokeWidth={2} aria-hidden />
                </span>
              </button>
              {openIndex === i && (
                <div className="px-5 pb-5">
                  <p className="text-[15px] leading-relaxed text-slate-700 sm:text-slate-600">{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
